<template>
  <div class="relative flex justify-between px-2 py-1 bg-white grow-0">
    <div class="my-auto ml-2">
      {{ $t('pages.' + (parentRoute as string)) }}
    </div>
    <div class="flex">
      <span class="my-auto text-blue-500" v-if="sessionDetails">{{ sessionDetails.userName }}</span>
      <div @click="onUserMenuClick">
        <img class="w-10 h-10 ml-3 rounded-full" src="@/assets/images/profile-pic.svg" />
      </div>
    </div>
  </div>
  <div
    v-if="showUserMenu"
    ref="userMenuTarget"
    class="absolute right-0 z-[60] mx-5 border rounded-lg shadow-md w-60">
    <div class="flex-col divide-y-2 border-gray-50">
      <div class="flex-col w-full px-3 pt-2 bg-gray-50" v-if="sessionDetails !== undefined">
        <div class="font-bold">{{ sessionDetails.userName }}</div>
        <div class="text-xs">{{ sessionDetails.userType }}</div>
        <div class="flex justify-between pt-1.5 text-xs text-gray-500">
          <span class="font-semibold">Last login</span>
          <span>{{ lastLogin }}</span>
        </div>
      </div>
      <div class="flex justify-center p-2 bg-white border-gray-50">
        <img class="w-4 h-4 my-auto" src="@/assets/images/ic-weather-sunny.svg" />
        <ToggleSwitch
          class="mx-2 cursor-pointer"
          v-model="darkModeEnabled"
          @update:model-value="darkModeUpdated" />
        <img class="w-4 h-4 my-auto" src="@/assets/images/moon-dark@2x.png" />
      </div>
      <div class="flex p-3 bg-white cursor-pointer" @click="onSettingsClicked">
        <img class="w-4 h-4 my-auto" src="@/assets/images/ic-actions-settings.svg" />
        <div class="pl-4 text-sm">Settings</div>
      </div>
      <div class="flex p-3 bg-white cursor-pointer" @click="onLogoutClicked">
        <img class="w-4 h-4 my-auto" src="@/assets/images/logout.svg" />
        <div class="pl-4 text-sm text-red-600">Log out</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import ToggleSwitch from './ToggleSwitch.vue';
import type { SessionDetails } from '@/types/Base';
import { isoDateToFullDateTime } from '@/utils/date';
import router from '@/router';
import { useRoute } from 'vue-router';
import { onClickOutside } from '@vueuse/core';
import { useAuthStore } from '@/stores/auth';

const route = useRoute();
const showUserMenu = ref(false);
const userMenuTarget = ref(null);
onClickOutside(userMenuTarget, () => (showUserMenu.value = false));

const darkModeEnabled = ref(false);
const sessionDetails = ref<SessionDetails | undefined>();
const lastLogin = ref<string | undefined>('');
const { logout } = useAuthStore();

onMounted(() => {
  const memorySession = localStorage.getItem('sessionDetails');
  if (memorySession) sessionDetails.value = JSON.parse(memorySession) as SessionDetails;
  if (sessionDetails.value) lastLogin.value = isoDateToFullDateTime(sessionDetails.value.lastAlive);
  const darkMode = localStorage.getItem('darkModeEnabled');
  if (darkMode) darkModeEnabled.value = darkMode === 'true';
});

function onUserMenuClick() {
  showUserMenu.value = !showUserMenu.value;
}

function onSettingsClicked() {
  router.push('/settings');
  showUserMenu.value = false;
}

function onLogoutClicked() {
  logout();
  router.push('/');
}

function darkModeUpdated() {
  localStorage.setItem('darkModeEnabled', JSON.stringify(darkModeEnabled.value));
}

const parentRoute = computed(() => {
  if (route.matched.length && route.matched[0].children.length) return route.matched[0].name;
  return route.name;
});
</script>
